import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ElementLibrary from './components/ElementLibrary';
import FormCanvas from './components/FormCanvas';
import SettingsPanel from './components/SettingsPanel';
import PreviewModal from './components/PreviewModal';
import SubmissionSettingsBar from './components/SubmissionSettingsBar';
import ConditionalLogicPanel from './components/ConditionalLogicPanel';
import MultiStepPanel from './components/MultiStepPanel';
import CalculatedFieldsPanel from './components/CalculatedFieldsPanel';
import GlobalThemeBuilder from './components/GlobalThemeBuilder';
import PerFieldStyling from './components/PerFieldStyling';
import PrebuiltThemes from './components/PrebuiltThemes';
import SubmissionsPanel from './components/SubmissionsPanel';
import SplitPanelSettings from './components/SplitPanelSettings';
import NewUserTooltips from './components/NewUserTooltips';
import { SaveToast, useSaveToast } from './components/SaveToast';
import { formsService } from '../../services/formsService';
import { userThemesService } from '../../services/userThemesService';
import { useAuth } from '../../contexts/AuthContext';
import { usePlanLimits } from '../../hooks/usePlanLimits';
import { formatLimit } from '../../config/planFeatures';

const FormBuilder = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const { canCreateForm, limits, planType: creatorPlanType, loading: planLoading } = usePlanLimits();
  const formId = searchParams?.get('id');
  const startBlank = searchParams?.get('new') === '1';

  const [showPlanLimitModal, setShowPlanLimitModal] = useState(false);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formElements, setFormElements] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null);
  const [history, setHistory] = useState([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [selectedElementId, setSelectedElementId] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const titleInputRef = React.useRef(null);
  const [isNewForm, setIsNewForm] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Feature panel state
  const [activeFeaturePanel, setActiveFeaturePanel] = useState(null); // 'layout' | 'logic' | 'multistep' | 'calcfields' | 'globaltheme' | 'perfield' | 'themes'
  const [logicRules, setLogicRules] = useState([]);
  // Top-level layout mode: 'standard' | 'multi-step' | 'conversational'
  const [layoutMode, setLayoutMode] = useState('standard');
  // Ref mirrors layoutMode so save closures always read the latest value
  // (same pattern as multiStepConfigRef — avoids stale-closure autosave bugs)
  const layoutModeRef = useRef('standard');
  // splitPanelConfig — stored inside settings JSONB alongside layoutMode.
  const [splitPanelConfig, setSplitPanelConfig] = useState(null);
  const splitPanelConfigRef = useRef(null);
  // Ref buffers the child's live config so child updates don't re-render the parent
  // (which was causing the stale-closure save bug).
  const multiStepConfigRef = useRef(null);
  const multiStepPanelKeyRef = useRef(0); // incremented only when panel opens
  const [calcFields, setCalcFields] = useState([]);
  const [globalTheme, setGlobalTheme] = useState({});
  const [multiStepConfig, setMultiStepConfig] = useState(null);
  // Tracks which step tab is active in FormCanvas so the PAGES panel can sync
  const [activePreviewStep, setActivePreviewStep] = useState(null);
  // Confirmation dialog for switching away from Multi-Step when pages are configured
  const [pendingLayoutMode, setPendingLayoutMode] = useState(null);
  const { toasts: builderToasts, addToast: addBuilderToast, dismissToast: dismissBuilderToast } = useSaveToast(2500);

  useEffect(() => {
    if (authLoading || planLoading) return;
    
    if (!user) {
      navigate('/authentication');
      return;
    }

    if (formId) {
      loadForm();
    } else {
      // Check if template data was passed from templates gallery
      const templateData = location?.state?.template;
      if (templateData) {
        // Template-based creation: gate on plan limit
        if (!canCreateForm()) {
          setShowPlanLimitModal(true);
          setLoading(false);
          return;
        }
        initializeFromTemplate(templateData);
      } else {
        // Check if there's a last edited form to restore
        const lastFormId = localStorage.getItem('lastEditedFormId');
        if (lastFormId && !startBlank) {
          navigate(`/form-builder?id=${lastFormId}`, { replace: true });
        } else {
          // New blank form: gate on plan limit
          if (!canCreateForm()) {
            setShowPlanLimitModal(true);
            setLoading(false);
            return;
          }
          // Initialize empty form state without creating in database
          initializeNewForm();
        }
      }
    }
  }, [formId, startBlank, user, authLoading, planLoading]);

  const loadForm = async () => {
    try {
      setLoading(true);
      const formData = await formsService?.getFormById(formId);
      if (formData) {
        setForm(formData);
        setFormElements(formData?.formElements || []);
        setHistory([formData?.formElements || []]);
        setHistoryIndex(0);
        setLastSaved(formData?.updatedAt);
        setIsNewForm(false);
        // Load feature data
        setLogicRules(formData?.logicRules || []);
        const loadedMSC = formData?.multiStepConfig || null;
        setMultiStepConfig(loadedMSC);
        multiStepConfigRef.current = loadedMSC;
        setCalcFields(formData?.calcFields || []);
        setGlobalTheme(formData?.globalTheme || {});
        // layoutMode is stored in settings JSONB (primary path, no new column required).
        // Fall back to deriving from the legacy conversationalMode toggle inside multiStepConfig.
        const resolvedLayoutMode =
          formData?.settings?.layoutMode
          || (loadedMSC?.conversationalMode === true ? 'conversational'
              : loadedMSC?.enabled ? 'multi-step'
              : 'standard');
        setLayoutMode(resolvedLayoutMode);
        layoutModeRef.current = resolvedLayoutMode;
        const loadedSPC = formData?.settings?.splitPanelConfig || null;
        setSplitPanelConfig(loadedSPC);
        splitPanelConfigRef.current = loadedSPC;
        // Persist last edited form ID
        localStorage.setItem('lastEditedFormId', formId);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Error loading form:', err);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const initializeNewForm = async () => {
    // Initialize empty form state without saving to database
    const defaultLogoElement = {
      id: `image_default_logo`,
      name: 'Logo/Image',
      label: 'Logo/Image',
      type: 'media',
      src: '/assets/images/web-forms-logo-1769800970615.png',
      alt: 'WebForms.biz logo',
      imageSize: 35,
      placeholder: '',
      required: false,
    };

    // Seed globalTheme from the creator's default user theme (if one is set)
    let seedTheme = {};
    try {
      const defaultRow = await userThemesService?.getDefaultTheme();
      if (defaultRow?.config) {
        seedTheme = { ...defaultRow.config, _themeId: defaultRow.id };
      }
    } catch (_) {
      // Non-fatal — fall back to hardcoded defaults
    }

    setForm({
      title: 'Untitled Form',
      type: 'Contact',
      status: 'draft',
      formElements: [defaultLogoElement],
      pages: [],
      styles: {},
      settings: {},
      logic: [],
      logicRules: [],
      multiStepConfig: null,
      calcFields: [],
      paymentEnabled: false,
      paymentSettings: {
        amount: 0,
        currency: 'USD',
        description: '',
        mode: 'fixed'
      }
    });
    setFormElements([defaultLogoElement]);
    setLogicRules([]);
    setMultiStepConfig(null);
    setCalcFields([]);
    setGlobalTheme(seedTheme);
    setHistory([[]]);
    setHistoryIndex(0);
    setIsNewForm(true);
    setLoading(false);
  };

  const initializeFromTemplate = (templateData) => {
    // Initialize form with template data
    const timestamp = Date.now();
    const templateElements = templateData?.formElements?.map((field, index) => ({
      ...field,
      id: `${field?.id}_${timestamp}_${index}`,
      label: field?.label || field?.name || '',
      placeholder: field?.placeholder || (field?.type === 'media' ? '' : `Enter ${(field?.name || 'value')?.toLowerCase()}...`),
      required: field?.required || false,
      options: field?.options || [],
      content: field?.content || '',
      // Preserve media-specific fields
      src: field?.src || '',
      alt: field?.alt || '',
    })) || [];

    setForm({
      title: templateData?.title || 'Untitled Form',
      type: templateData?.category || 'Contact',
      status: 'draft',
      formElements: templateElements,
      pages: [],
      styles: {},
      settings: {},
      logic: [],
      logicRules: [],
      multiStepConfig: null,
      calcFields: [],
      paymentEnabled: false,
      paymentSettings: {
        amount: 0,
        currency: 'USD',
        description: '',
        mode: 'fixed',
      },
    });
    setFormElements(templateElements);
    setLogicRules([]);
    setMultiStepConfig(null);
    setCalcFields([]);
    setHistory([templateElements]);
    setHistoryIndex(0);
    setIsNewForm(true);
    setLoading(false);
  };

  const createNewForm = async (globalThemeOverride = undefined) => {
    // Only called when user actually saves or adds first element.
    // globalThemeOverride lets callers (e.g. saveForm) pass an explicit theme
    // that may not yet be reflected in the globalTheme state closure.
    try {
      setSaving(true);
      const newForm = await formsService?.createForm({
        title: form?.title || 'Untitled Form',
        type: form?.type || 'Contact',
        status: 'draft',
        formElements: formElements,
        pages: form?.pages || [],
        styles: form?.styles || {},
        // layoutMode is stored inside settings JSONB so no new column is required.
        // Use the ref so this closure always captures the latest selected mode.
        settings: { ...(form?.settings || {}), layoutMode: layoutModeRef.current, splitPanelConfig: splitPanelConfigRef.current },
        logic: form?.logic || [],
        logicRules: logicRules,
        multiStepConfig: multiStepConfig,
        calcFields: calcFields,
        globalTheme: globalThemeOverride !== undefined ? globalThemeOverride : globalTheme,
        paymentEnabled: form?.paymentEnabled || false,
        paymentSettings: form?.paymentSettings || {
          amount: 0,
          currency: 'USD',
          description: '',
          mode: 'fixed'
        }
      });
      if (newForm) {
        setForm(newForm);
        setIsNewForm(false);
        navigate(`/form-builder?id=${newForm?.id}`, { replace: true });
        return newForm;
      }
    } catch (err) {
      console.error('Error creating form:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const saveForm = async (elementsToSave = formElements, globalThemeOverride = undefined) => {
    // If this is a new form that hasn't been saved yet, create it first.
    // Pass the override through so the seeded default theme is persisted even
    // when React state hasn't flushed yet.
    if (isNewForm) {
      const newForm = await createNewForm(globalThemeOverride);
      return newForm;
    }
    
    if (!form?.id || saving) return;
    
    try {
      setSaving(true);
      setSaveError(null);
      // Read from ref so we always get the latest config regardless of render cycle
      const latestMSC = multiStepConfigRef?.current;
      const updated = await formsService?.updateForm(form?.id, {
        formElements: elementsToSave,
        paymentEnabled: form?.paymentEnabled,
        paymentSettings: form?.paymentSettings,
        logicRules: logicRules,
        multiStepConfig: latestMSC,
        calcFields: calcFields,
        globalTheme: globalThemeOverride !== undefined ? globalThemeOverride : globalTheme,
        // layoutMode and splitPanelConfig in settings JSONB; refs ensure closures capture latest values
        settings: { ...(form?.settings || {}), layoutMode: layoutModeRef.current, splitPanelConfig: splitPanelConfigRef.current },
        updatedAt: new Date()?.toISOString()
      });
      if (updated) {
        setLastSaved(updated?.updatedAt);
        // Sync state from ref after a successful save so badge stays accurate
        setMultiStepConfig(latestMSC);
        return true;
      } else {
        setSaveError('Save failed. Changes may not persist.');
        return false;
      }
    } catch (err) {
      console.error('Error saving form:', err);
      setSaveError('Save failed. Changes may not persist.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleTitleClick = () => {
    setIsEditingTitle(true);
    setEditedTitle(form?.title || 'Untitled Form');
  };

  const handleTitleSave = async () => {
    if (!editedTitle?.trim()) {
      setIsEditingTitle(false);
      return;
    }

    // Update local state
    setForm({ ...form, title: editedTitle?.trim() });

    // If this is a new form, just update local state
    if (isNewForm) {
      setIsEditingTitle(false);
      return;
    }

    // If form exists, save to database
    if (!form?.id) {
      setIsEditingTitle(false);
      return;
    }

    try {
      const updated = await formsService?.updateForm(form?.id, {
        title: editedTitle?.trim()
      });
      if (updated) {
        setForm({ ...form, title: editedTitle?.trim() });
      }
    } catch (err) {
      console.error('Error updating form title:', err);
    } finally {
      setIsEditingTitle(false);
    }
  };

  const handleTitleKeyDown = (e) => {
    if (e?.key === 'Enter') {
      e?.preventDefault();
      handleTitleSave();
    } else if (e?.key === 'Escape') {
      setIsEditingTitle(false);
      setEditedTitle(form?.title || 'Untitled Form');
    }
  };

  const handleTitleBlur = () => {
    handleTitleSave();
  };

  useEffect(() => {
    if (isEditingTitle && titleInputRef?.current) {
      titleInputRef?.current?.focus();
      titleInputRef?.current?.select();
    }
  }, [isEditingTitle]);

  // Auto-save every 30 seconds
  useEffect(() => {
    if (!form?.id) return;
    
    const interval = setInterval(() => {
      saveForm(formElements);
    }, 30000);

    return () => clearInterval(interval);
  }, [form?.id, formElements, form?.paymentEnabled, form?.paymentSettings, logicRules, multiStepConfig, calcFields, layoutMode]);

  // REMOVED: debounced multiStepConfig auto-save useEffect (was causing re-render loops
  // that overwrote MultiStepPanel's local state immediately after toggle).
  // multiStepConfig is now saved via the regular 30s interval and the manual Save button.

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e?.ctrlKey || e?.metaKey) && e?.key === 'z' && !e?.shiftKey) {
        e?.preventDefault();
        handleUndo();
      }
      if ((e?.ctrlKey || e?.metaKey) && (e?.key === 'y' || (e?.key === 'z' && e?.shiftKey))) {
        e?.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [historyIndex]);

  const addToHistory = (newElements) => {
    const newHistory = history?.slice(0, historyIndex + 1);
    newHistory?.push(newElements);
    setHistory(newHistory);
    setHistoryIndex(newHistory?.length - 1);
  };

  const handleElementClick = (elementData) => {
    const newElement = {
      ...elementData,
      id: `${elementData?.id}_${Date.now()}`,
      label: elementData?.name,
      placeholder: `Enter ${elementData?.name?.toLowerCase()}...`,
      required: false
    };

    const newElements = [...formElements, newElement];
    setFormElements(newElements);
    addToHistory(newElements);
    setSelectedElement(newElement);
    setSelectedElementId(newElement?.id);
    setShowSettings(true);
    
    // Auto-save when first element is added to a new form
    if (isNewForm && formElements?.length === 0) {
      saveForm(newElements);
    }
  };

  // Add a new element directly to a specific multi-step page
  const handleAddElementToStep = (elementData, stepId) => {
    const newElement = {
      ...elementData,
      id: `${elementData?.id}_${Date.now()}`,
      label: elementData?.name,
      placeholder: `Enter ${elementData?.name?.toLowerCase()}...`,
      required: false
    };

    const newElements = [...formElements, newElement];
    setFormElements(newElements);
    addToHistory(newElements);
    setSelectedElement(newElement);
    setSelectedElementId(newElement?.id);
    setShowSettings(false);

    // Assign the new element to the target step in multiStepConfig
    const currentConfig = multiStepConfigRef?.current || multiStepConfig;
    if (currentConfig?.steps) {
      const updatedConfig = {
        ...currentConfig,
        steps: currentConfig?.steps?.map((s) =>
          s?.id === stepId
            ? { ...s, fieldIds: [...(s?.fieldIds || []), newElement?.id] }
            : s
        ),
      };
      multiStepConfigRef.current = updatedConfig;
      setMultiStepConfig(updatedConfig);
      // Bump panel key so MultiStepPanel re-initializes with the updated config
      multiStepPanelKeyRef.current += 1;
    }

    if (isNewForm && formElements?.length === 0) {
      saveForm(newElements);
    }
  };

  const handleTemplateClick = (template) => {
    // Create multiple elements from template fields
    const timestamp = Date.now();
    const newElements = template?.fields?.map((field, index) => ({
      ...field,
      id: `${field?.id}_${timestamp}_${index}`,
      label: field?.label || field?.name,
      placeholder: field?.placeholder || `Enter ${field?.name?.toLowerCase()}...`,
      required: field?.required || false,
      options: field?.options || []
    }));

    const updatedElements = [...formElements, ...newElements];
    setFormElements(updatedElements);
    addToHistory(updatedElements);
    
    // Select the first element of the template
    if (newElements?.length > 0) {
      setSelectedElement(newElements?.[0]);
      setSelectedElementId(newElements?.[0]?.id);
      setShowSettings(true);
    }
    
    // Auto-save when first template is added to a new form
    if (isNewForm && formElements?.length === 0) {
      saveForm(updatedElements);
    }
  };

  const handleElementSelect = (element) => {
    setSelectedElement(element);
    setSelectedElementId(element?.id);
    setShowSettings(true);
    setActiveFeaturePanel(null);
  };

  const handleUpdateElement = (updatedElement) => {
    const newElements = formElements?.map(el =>
      el?.id === updatedElement?.id ? updatedElement : el
    );
    setFormElements(newElements);
    setSelectedElement(updatedElement);
    setSelectedElementId(updatedElement?.id);
    addToHistory(newElements);
  };

  // Apply a style patch to every element on the canvas.
  // mode='fieldStyle' (default) → merges into each el.fieldStyle
  // mode='direct'               → merges directly onto the element (type-filtered via typeFilter)
  const handleApplyStyleToAll = (patch, { mode = 'fieldStyle', typeFilter = null } = {}) => {
    setFormElements(prev => {
      const updated = prev.map(el => {
        if (typeFilter && el?.type !== typeFilter) return el;
        if (mode === 'fieldStyle') {
          return { ...el, fieldStyle: { ...(el?.fieldStyle || {}), ...patch } };
        }
        return { ...el, ...patch };
      });
      addToHistory(updated);
      return updated;
    });
  };

  const handleElementDelete = (elementId) => {
    const newElements = formElements?.filter(el => el?.id !== elementId);
    setFormElements(newElements);
    addToHistory(newElements);
    setSelectedElement(null);
    setShowSettings(false);
  };

  const handleDuplicateElement = (elementId) => {
    const sourceIndex = formElements?.findIndex(el => el?.id === elementId);
    if (sourceIndex === -1) return;
    const source = formElements?.[sourceIndex];
    // Deep-clone all settings; assign a fresh unique ID
    const duplicate = {
      ...JSON.parse(JSON.stringify(source)),
      id: `${source?.id?.split('_')[0]}_${Date.now()}`,
    };
    const newElements = [
      ...formElements?.slice(0, sourceIndex + 1),
      duplicate,
      ...formElements?.slice(sourceIndex + 1),
    ];
    setFormElements(newElements);
    addToHistory(newElements);

    // If multi-step is active, insert the duplicate ID right after the source
    // ID in whichever step owns the source element.
    const currentConfig = multiStepConfigRef?.current || multiStepConfig;
    if (currentConfig?.enabled && Array.isArray(currentConfig?.steps)) {
      const updatedSteps = currentConfig.steps.map(step => {
        const ids = step?.fieldIds || [];
        const pos = ids.indexOf(source?.id);
        if (pos === -1) return step; // source not in this step
        const newIds = [
          ...ids.slice(0, pos + 1),
          duplicate.id,
          ...ids.slice(pos + 1),
        ];
        return { ...step, fieldIds: newIds };
      });
      const updatedConfig = { ...currentConfig, steps: updatedSteps };
      multiStepConfigRef.current = updatedConfig;
      setMultiStepConfig(updatedConfig);
    }

    // Select the duplicate so its settings panel opens immediately
    setSelectedElement(duplicate);
    setSelectedElementId(duplicate?.id);
    setShowSettings(true);
  };

  const handleElementsReorder = (newElements) => {
    setFormElements(newElements);
    addToHistory(newElements);
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setFormElements(history?.[newIndex]);
      setSelectedElement(null);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history?.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setFormElements(history?.[newIndex]);
      setSelectedElement(null);
    }
  };

  const handleClear = () => {
    if (window.confirm('Are you sure you want to clear all elements?')) {
      setFormElements([]);
      addToHistory([]);
      setSelectedElement(null);
      setShowSettings(false);
    }
  };

  const handleSave = async () => {
    await saveForm();
  };

  const handlePreview = () => {
    setShowPreview(true);
  };

  const handlePublish = async () => {
    // If this is a new form, save it first
    if (isNewForm) {
      const newForm = await createNewForm();
      if (!newForm?.id) return;
    }

    if (!form?.id) return;

    try {
      // Include settings so the layoutMode is always persisted before publishing,
      // even if autosave hasn't fired since the last mode change.
      await formsService?.updateForm(form?.id, {
        status: 'published',
        published: true,
        settings: { ...(form?.settings || {}), layoutMode: layoutModeRef.current, splitPanelConfig: splitPanelConfigRef.current }
      });
      setForm({ ...form, status: 'published' });
      navigate(`/publish-screen?id=${form?.id}`);
    } catch (err) {
      console.error('Error publishing form:', err);
    }
  };

  const toggleFeaturePanel = (panel) => {
    if (activeFeaturePanel === panel) {
      // Panel closing — sync ref → state so badge updates
      if (panel === 'multistep') {
        setMultiStepConfig(multiStepConfigRef?.current);
      }
      setActiveFeaturePanel(null);
    } else {
      // Panel opening — bump the key so MultiStepPanel re-initializes with latest config
      if (panel === 'multistep') {
        multiStepPanelKeyRef.current += 1;
      }
      setActiveFeaturePanel(panel);
      if (!['perfield']?.includes(panel)) {
        setShowSettings(false);
        setSelectedElement(null);
        setSelectedElementId(null);
      }
    }
  };

  const handleSplitPanelConfigChange = async (newConfig) => {
    setSplitPanelConfig(newConfig);
    splitPanelConfigRef.current = newConfig;
    if (!isNewForm && form?.id) {
      const ok = await saveForm(formElements);
      if (ok) addBuilderToast('Layout settings saved.', 'success');
      else addBuilderToast('Failed to save layout settings.', 'error');
    }
  };

  const applyLayoutModeChange = (mode) => {
    setLayoutMode(mode);
    layoutModeRef.current = mode;
    // When switching to multi-step, ensure multiStepConfig has at least a default enabled config
    if (mode === 'multi-step') {
      const current = multiStepConfigRef.current || multiStepConfig;
      if (!current?.enabled) {
        const newConfig = {
          enabled: true,
          steps: [{ id: `step_${Date.now()}`, title: 'Page 1', description: '', fieldIds: [] }],
          progressStyle: 'bar',
          progressPosition: 'top',
          showStepTitles: true,
          showStepCount: true,
          nextButtonText: 'Next',
          prevButtonText: 'Previous',
          submitButtonText: 'Submit',
          allowStepJump: false,
          validateOnNext: true,
        };
        multiStepConfigRef.current = newConfig;
        setMultiStepConfig(newConfig);
        multiStepPanelKeyRef.current += 1;
      }
    }
    // Persist the change immediately so navigating away or clicking Publish
    // always captures the selected mode, regardless of the 30-second autosave cycle.
    if (!isNewForm && form?.id) {
      saveForm(formElements).then(ok => {
        if (ok) addBuilderToast('Layout settings saved.', 'success');
        else addBuilderToast('Failed to save layout settings.', 'error');
      });
    }
  };

  // Count enabled logic rules that reference fields assigned to specific
  // multi-step pages (via conditions or field targets). Used to warn the
  // creator that switching layout mode may affect those rules.
  const countLogicRulesReferencingStepFields = () => {
    const currentConfig = multiStepConfigRef.current || multiStepConfig;
    const steps = currentConfig?.steps || [];
    const stepFieldIds = new Set(steps.flatMap(s => s?.fieldIds || []));
    const stepPageIds = new Set(steps.map(s => s?.id).filter(Boolean));
    let fieldRuleCount = 0;
    let pageRuleCount = 0;
    (logicRules || []).forEach(rule => {
      if (rule?.enabled === false) return;
      const referencesCondition = (rule?.conditions || []).some(c => c?.fieldId && stepFieldIds.has(c.fieldId));
      const referencesTarget = rule?.targetType !== 'page' && rule?.targetFieldId && stepFieldIds.has(rule.targetFieldId);
      const referencesPage = rule?.targetType === 'page' && rule?.targetPageId && stepPageIds.has(rule.targetPageId);
      if (referencesPage) pageRuleCount += 1;
      else if (referencesCondition || referencesTarget) fieldRuleCount += 1;
    });
    return { fieldRuleCount, pageRuleCount, total: fieldRuleCount + pageRuleCount };
  };

  const handleLayoutModeChange = (mode) => {
    // If switching away from multi-step and pages have been configured, confirm first
    if (layoutMode === 'multi-step' && mode !== 'multi-step') {
      const currentConfig = multiStepConfigRef.current || multiStepConfig;
      const hasPages = currentConfig?.steps && currentConfig.steps.length > 0;
      if (hasPages) {
        setPendingLayoutMode(mode);
        return;
      }
    }
    applyLayoutModeChange(mode);
  };

  const featurePanelBadge = (panel) => {
    if (panel === 'layout') return layoutMode !== 'standard' ? 1 : 0;
    if (panel === 'logic') return logicRules?.filter(r => r?.enabled)?.length || 0;
    if (panel === 'multistep') return multiStepConfig?.enabled ? multiStepConfig?.steps?.length || 0 : 0;
    if (panel === 'calcfields') return calcFields?.filter(f => f?.enabled)?.length || 0;
    if (panel === 'globaltheme') return Object.keys(globalTheme || {})?.filter(k => k !== '_themeId')?.length > 0 ? 1 : 0;
    if (panel === 'themes') return globalTheme?._themeId ? 1 : 0;
    if (panel === 'responses') return form?.submissionsCount || 0;
    return 0;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar stats={{}}>
          <div className="flex items-center justify-center h-[calc(100vh-64px)]">
            <div className="text-center">
              <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading form builder...</p>
            </div>
          </div>
        </NavigationBar>
      </div>
    );
  }

  // Plan limit gate — shown when the creator is at their form limit and tries to create a new form
  if (showPlanLimitModal) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar stats={{}}>
          <div className="flex items-center justify-center h-[calc(100vh-64px)] px-4">
            <div className="w-full max-w-md bg-card border border-border rounded-xl shadow-elevation-lg p-8 text-center">
              <div className="w-14 h-14 rounded-full bg-error/10 flex items-center justify-center mx-auto mb-4">
                <Icon name="AlertTriangle" size={28} color="var(--color-error)" />
              </div>
              <h2 className="text-xl font-heading font-semibold text-foreground mb-2">
                Form limit reached
              </h2>
              <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                You've used all{' '}
                <strong>{formatLimit(limits.maxForms)} form{limits.maxForms === 1 ? '' : 's'}</strong>{' '}
                on your{' '}
                <strong className="capitalize">{creatorPlanType}</strong> plan.
                Upgrade to create more forms.
              </p>
              <div className="bg-error/5 border border-error/20 rounded-lg p-3 mb-6">
                <div className="flex items-center justify-center gap-2">
                  <Icon name="FileText" size={14} color="var(--color-error)" />
                  <span className="text-sm font-semibold text-error">
                    {formatLimit(limits.maxForms)} / {formatLimit(limits.maxForms)} forms used
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-3">
                <Button
                  variant="default"
                  iconName="Zap"
                  iconPosition="left"
                  onClick={() => navigate('/account-settings?tab=billing')}
                >
                  Upgrade Plan
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                >
                  Back to Dashboard
                </Button>
              </div>
            </div>
          </div>
        </NavigationBar>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar stats={{}}>
        {/* Header with form title and actions */}
        <div className="border-b border-border bg-card">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="p-2 hover:bg-muted rounded-md transition-smooth"
                >
                  <Icon name="ArrowLeft" size={20} />
                </button>
                <div>
                  {isEditingTitle ? (
                    <input
                      ref={titleInputRef}
                      type="text"
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e?.target?.value)}
                      onKeyDown={handleTitleKeyDown}
                      onBlur={handleTitleBlur}
                      className="text-xl font-heading font-semibold text-foreground bg-transparent border-b-2 border-primary focus:outline-none px-1 py-0.5"
                      maxLength={100}
                    />
                  ) : (
                    <h1 
                      className="text-xl font-heading font-semibold text-foreground cursor-pointer hover:text-primary transition-colors"
                      onClick={handleTitleClick}
                      title="Click to edit form name"
                    >
                      {form?.title || 'Untitled Form'}
                    </h1>
                  )}
                  <p className="text-sm text-muted-foreground">
                    {saving ? 'Saving...' : saveError ? (
                      <span className="text-destructive text-xs">{saveError}</span>
                    ) : lastSaved ? `Last saved: ${new Date(lastSaved)?.toLocaleTimeString()}` : 'Not saved yet'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleUndo}
                  disabled={historyIndex === 0}
                >
                  <Icon name="Undo" size={16} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRedo}
                  disabled={historyIndex === history?.length - 1}
                >
                  <Icon name="Redo" size={16} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClear}
                  disabled={formElements?.length === 0}
                >
                  <Icon name="Trash2" size={16} />
                  Clear
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePreview}
                >
                  <Icon name="Eye" size={16} />
                  Preview
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSave}
                  disabled={saving}
                >
                  <Icon name={saving ? "Loader2" : "Save"} size={16} className={saving ? "animate-spin" : ""} />
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handlePublish}
                >
                  <Icon name="Upload" size={16} />
                  Publish
                </Button>
              </div>
            </div>
          </div>

          {/* Feature toolbar */}
          <div className="px-6 pb-0 flex items-center gap-1 border-t border-border/50 overflow-x-auto scrollbar-custom">
            {[
              { id: 'layout', icon: 'LayoutGrid', label: 'Form Layout' },
              { id: 'logic', icon: 'GitBranch', label: 'Conditional Logic' },
              { id: 'multistep', icon: 'Layers', label: 'Multi-Step' },
              { id: 'calcfields', icon: 'Calculator', label: 'Calculated Fields' },
              { id: 'themes', icon: 'Sparkles', label: 'Themes' },
              { id: 'globaltheme', icon: 'Palette', label: 'Global Theme' },
              { id: 'perfield', icon: 'Sliders', label: 'Field Styling' },
              { id: 'responses', icon: 'Inbox', label: 'Responses' },
            ]?.map(feat => {
              const badge = featurePanelBadge(feat?.id);
              const isActive = activeFeaturePanel === feat?.id;
              return (
                <button
                  key={feat?.id}
                  onClick={() => toggleFeaturePanel(feat?.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors relative whitespace-nowrap flex-shrink-0 ${
                    isActive
                      ? 'border-primary text-primary' :'border-transparent hover:border-border'
                  }`}
                  style={!isActive ? { color: '#007876' } : {}}
                >
                  <Icon name={feat?.icon} size={14} color={isActive ? 'var(--color-primary)' : '#1a1a1a'} />
                  <span>{feat?.label}</span>
                  {badge > 0 && (
                    <span className={`ml-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${isActive ? 'bg-primary text-primary-foreground' : 'bg-muted-foreground/20 text-foreground'}`}>
                      {badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Main content area */}
        <div className="flex h-[calc(100vh-160px)] overflow-hidden">
          {/* Element Library - Left sidebar */}
          <ElementLibrary 
            onElementClick={handleElementClick} 
            onTemplateClick={handleTemplateClick}
            onAddElement={handleAddElementToStep}
          />
          
          {/* Form Canvas - Center */}
          <div className="flex-1 overflow-auto bg-muted/30">
            <SubmissionSettingsBar formId={form?.id} formTitle={form?.title} />
            <FormCanvas
              formElements={formElements}
              onElementSelect={handleElementSelect}
              selectedElementId={selectedElementId}
              onElementDelete={handleElementDelete}
              onDuplicate={handleDuplicateElement}
              multiStepConfig={multiStepConfig}
              currentStep={multiStepConfig?.currentStep ?? 0}
              globalTheme={globalTheme}
              onReorder={handleElementsReorder}
              onUpdateElement={handleUpdateElement}
              onPreviewStepChange={setActivePreviewStep}
              layoutMode={layoutMode}
              onOpenLayoutPanel={() => toggleFeaturePanel('layout')}
              splitPanelConfig={splitPanelConfig}
            />
          </div>

          {/* Right panel — feature panels or settings panel */}
          {activeFeaturePanel === 'logic' && (
            <div className="w-80 lg:w-80 md:w-64 bg-card border-l border-border flex flex-col flex-shrink-0 overflow-hidden">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon name="GitBranch" size={18} color="var(--color-primary)" />
                  <h3 className="text-base font-heading font-semibold text-foreground">Conditional Logic</h3>
                </div>
                <button onClick={() => setActiveFeaturePanel(null)} className="p-1.5 rounded-md hover:bg-muted transition-smooth">
                  <Icon name="X" size={16} color="var(--color-muted-foreground)" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <ConditionalLogicPanel
                  formElements={formElements}
                  logicRules={logicRules}
                  onUpdateRules={setLogicRules}
                  multiStepConfig={multiStepConfig}
                />
              </div>
            </div>
          )}

          {activeFeaturePanel === 'multistep' && (
            <div className="w-80 lg:w-80 md:w-64 bg-card border-l border-border flex flex-col flex-shrink-0 overflow-hidden">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon name="Layers" size={18} color="var(--color-primary)" />
                  <h3 className="text-base font-heading font-semibold text-foreground">Multi-Step Form</h3>
                </div>
                <button
                  onClick={() => {
                    // Sync ref → state on close so badge updates
                    setMultiStepConfig(multiStepConfigRef?.current);
                    setActiveFeaturePanel(null);
                  }}
                  className="p-1.5 rounded-md hover:bg-muted transition-smooth"
                >
                  <Icon name="X" size={16} color="var(--color-muted-foreground)" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <MultiStepPanel
                  key={`multistep-panel-${multiStepPanelKeyRef?.current}`}
                  formElements={formElements}
                  initialConfig={multiStepConfig}
                  onUpdateConfig={(next) => {
                    // Update ref for save operations
                    multiStepConfigRef.current = next;
                    // Also update state so FormCanvas receives live prop updates
                    // (progress style changes, toggle off, etc.)
                    setMultiStepConfig(next);
                  }}
                  onAddElement={handleAddElementToStep}
                  onDeleteElement={handleElementDelete}
                  globalTheme={globalTheme}
                  activeStep={activePreviewStep}
                />
              </div>
            </div>
          )}

          {activeFeaturePanel === 'calcfields' && (
            <div className="w-80 lg:w-80 md:w-64 bg-card border-l border-border flex flex-col flex-shrink-0 overflow-hidden">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon name="Calculator" size={18} color="var(--color-primary)" />
                  <h3 className="text-base font-heading font-semibold text-foreground">Calculated Fields</h3>
                </div>
                <button onClick={() => setActiveFeaturePanel(null)} className="p-1.5 rounded-md hover:bg-muted transition-smooth">
                  <Icon name="X" size={16} color="var(--color-muted-foreground)" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <CalculatedFieldsPanel
                  formElements={formElements}
                  calcFields={calcFields}
                  onUpdateCalcFields={setCalcFields}
                />
              </div>
            </div>
          )}

          {activeFeaturePanel === 'themes' && (
            <div className="w-80 lg:w-80 md:w-64 bg-card border-l border-border flex flex-col flex-shrink-0 overflow-hidden">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon name="Sparkles" size={18} color="var(--color-primary)" />
                  <h3 className="text-base font-heading font-semibold text-foreground">Pre-built Themes</h3>
                </div>
                <button onClick={() => setActiveFeaturePanel(null)} className="p-1.5 rounded-md hover:bg-muted transition-smooth">
                  <Icon name="X" size={16} color="var(--color-muted-foreground)" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <PrebuiltThemes
                  globalTheme={globalTheme}
                  onUpdateTheme={setGlobalTheme}
                  multiStepConfig={multiStepConfig}
                  onApplyTheme={async (newTheme) => {
                    setGlobalTheme(newTheme);
                    const ok = await saveForm(formElements, newTheme);
                    if (ok) addBuilderToast('Theme applied and saved.', 'success');
                    else addBuilderToast('Failed to save theme.', 'error');
                  }}
                />
              </div>
            </div>
          )}

          {activeFeaturePanel === 'globaltheme' && (
            <div className="w-80 lg:w-80 md:w-64 bg-card border-l border-border flex flex-col flex-shrink-0 overflow-hidden">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon name="Palette" size={18} color="var(--color-primary)" />
                  <h3 className="text-base font-heading font-semibold text-foreground">Global Theme</h3>
                </div>
                <button onClick={() => setActiveFeaturePanel(null)} className="p-1.5 rounded-md hover:bg-muted transition-smooth">
                  <Icon name="X" size={16} color="var(--color-muted-foreground)" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <GlobalThemeBuilder
                  globalTheme={globalTheme}
                  onUpdateTheme={setGlobalTheme}
                  multiStepConfig={multiStepConfig}
                  formElements={formElements}
                  onUpdateAllElements={(updated) => {
                    setFormElements(updated);
                    addToHistory(updated);
                  }}
                />
              </div>
            </div>
          )}

          {activeFeaturePanel === 'perfield' && (
            <div className="w-80 lg:w-80 md:w-64 bg-card border-l border-border flex flex-col flex-shrink-0 overflow-hidden">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon name="Sliders" size={18} color="var(--color-primary)" />
                  <h3 className="text-base font-heading font-semibold text-foreground">Field Styling</h3>
                </div>
                <button onClick={() => setActiveFeaturePanel(null)} className="p-1.5 rounded-md hover:bg-muted transition-smooth">
                  <Icon name="X" size={16} color="var(--color-muted-foreground)" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <PerFieldStyling
                  selectedElement={selectedElement}
                  onUpdateElement={handleUpdateElement}
                />
              </div>
            </div>
          )}

          {activeFeaturePanel === 'layout' && (
            <div className="w-80 lg:w-80 md:w-64 bg-card border-l border-border flex flex-col flex-shrink-0 overflow-hidden">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon name="LayoutGrid" size={18} color="var(--color-primary)" />
                  <h3 className="text-base font-heading font-semibold text-foreground">Form Layout</h3>
                </div>
                <button onClick={() => setActiveFeaturePanel(null)} className="p-1.5 rounded-md hover:bg-muted transition-smooth">
                  <Icon name="X" size={16} color="var(--color-muted-foreground)" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                <p className="text-xs text-muted-foreground">Choose how respondents experience your form.</p>
                {[
                  { value: 'standard', icon: 'FileText', label: 'Standard', desc: 'All fields on a single scrollable page.' },
                  { value: 'multi-step', icon: 'Layers', label: 'Multi-Step', desc: 'Split into multiple pages with progress tracking.' },
                  { value: 'conversational', icon: 'MessageCircle', label: 'Conversational', desc: 'One question at a time with smooth slide transitions.' },
                  { value: 'split', icon: 'PanelLeft', label: 'Split Page', desc: 'Side-by-side: form on one side, image, video, or text on the other.' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleLayoutModeChange(opt.value)}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                      layoutMode === opt.value
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/40'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Icon
                        name={opt.icon}
                        size={18}
                        color={layoutMode === opt.value ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                        className="flex-shrink-0 mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground">{opt.label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{opt.desc}</p>
                      </div>
                      {layoutMode === opt.value && (
                        <Icon name="CheckCircle2" size={16} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                      )}
                    </div>
                  </button>
                ))}
                {layoutMode === 'multi-step' && (
                  <button
                    type="button"
                    onClick={() => setActiveFeaturePanel('multistep')}
                    className="w-full flex items-center justify-center gap-2 py-2 text-xs font-medium text-primary border border-primary/30 rounded-lg hover:bg-primary/5 transition-colors"
                  >
                    <Icon name="Layers" size={13} />
                    Configure pages →
                  </button>
                )}
                {layoutMode === 'conversational' && (
                  <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
                    <p className="text-xs font-semibold text-foreground mb-1">One question at a time</p>
                    <p className="text-xs text-muted-foreground">Respondents move through questions with smooth slide animations. Use Preview to experience the flow.</p>
                  </div>
                )}
                {layoutMode === 'split' && (
                  <SplitPanelSettings
                    config={splitPanelConfig}
                    onUpdate={handleSplitPanelConfigChange}
                  />
                )}
              </div>
            </div>
          )}

          {activeFeaturePanel === 'responses' && (
            <div className="w-80 lg:w-80 md:w-64 bg-card border-l border-border flex flex-col flex-shrink-0 overflow-hidden">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon name="Inbox" size={18} color="var(--color-primary)" />
                  <h3 className="text-base font-heading font-semibold text-foreground">Responses</h3>
                </div>
                <button onClick={() => setActiveFeaturePanel(null)} className="p-1.5 rounded-md hover:bg-muted transition-smooth">
                  <Icon name="X" size={16} color="var(--color-muted-foreground)" />
                </button>
              </div>
              <div className="flex-1 overflow-hidden flex flex-col">
                <SubmissionsPanel formId={form?.id} formElements={formElements} form={form} />
              </div>
            </div>
          )}

          {!activeFeaturePanel && showSettings && selectedElement && (
            <SettingsPanel
              selectedElement={selectedElement}
              onUpdateElement={handleUpdateElement}
              onApplyStyleToAll={handleApplyStyleToAll}
              onClose={() => setShowSettings(false)}
              formId={form?.id}
              formTitle={form?.title}
              form={form}
              onUpdateForm={setForm}
              allElements={formElements}
              onElementDelete={handleElementDelete}
              onSave={handleSave}
            />
          )}
        </div>

        {/* Preview Modal */}
        {showPreview && (
          <PreviewModal
            isOpen={showPreview}
            formElements={formElements}
            formTitle={form?.title || 'Untitled Form'}
            onClose={() => setShowPreview(false)}
            globalTheme={globalTheme}
            multiStepConfig={multiStepConfig}
            onUpdateTheme={setGlobalTheme}
            formId={form?.id || null}
            paymentEnabled={form?.paymentEnabled || false}
            paymentSettings={form?.paymentSettings || null}
            layoutMode={layoutMode}
            splitPanelConfig={splitPanelConfig}
          />
        )}

        {/* New-user onboarding tooltips */}
        <NewUserTooltips isNewForm={isNewForm} formElements={formElements} />

        {/* Builder-level save toast (layout & theme saves) */}
        <SaveToast toasts={builderToasts} onDismiss={dismissBuilderToast} />

        {/* Confirmation dialog: switching away from Multi-Step when pages are configured */}
        {pendingLayoutMode !== null && (() => {
          const { fieldRuleCount, pageRuleCount, total: affectedRuleCount } = countLogicRulesReferencingStepFields();
          const affectedParts = [];
          if (fieldRuleCount > 0) affectedParts.push(`${fieldRuleCount} logic rule${fieldRuleCount !== 1 ? 's' : ''} reference${fieldRuleCount === 1 ? 's' : ''} fields assigned to your pages`);
          if (pageRuleCount > 0) affectedParts.push(`${pageRuleCount} logic rule${pageRuleCount !== 1 ? 's' : ''} target${pageRuleCount === 1 ? 's' : ''} pages directly`);
          const affectedSummary = affectedParts.join(', and ');
          return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-card border border-border rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
              <div className="flex items-start gap-3 mb-4">
                <div className="flex-shrink-0 mt-0.5">
                  <Icon name="AlertTriangle" size={20} color="var(--color-warning, #f59e0b)" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-1">Switch layout mode?</h3>
                  <p className="text-sm text-muted-foreground">
                    Your page assignments will be preserved but inactive. Switch anyway?
                  </p>
                  {affectedRuleCount > 0 && (
                    <div className="mt-3 p-2.5 bg-warning/10 border border-warning/30 rounded-lg flex items-start gap-2">
                      <Icon name="GitBranch" size={14} color="var(--color-warning, #f59e0b)" className="mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-foreground">
                        {affectedSummary}. Without step context,
                        {affectedRuleCount !== 1 ? ' these rules' : ' this rule'} may behave differently after switching. No rules will
                        be changed — review them in the Logic panel if needed.
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPendingLayoutMode(null)}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => {
                    const mode = pendingLayoutMode;
                    setPendingLayoutMode(null);
                    applyLayoutModeChange(mode);
                  }}
                >
                  Switch anyway
                </Button>
              </div>
            </div>
          </div>
          );
        })()}
      </NavigationBar>
    </div>
  );
};

export default FormBuilder;